import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { trackConversion } from '@/lib/analytics';
import { formatInteger } from '@/lib/language';
import { BeforeAfterDiff } from '@/components/shared/BeforeAfterDiff';

interface FeedCheckInsight {
  product_id: string;
  original_title: string;
  issue: string;
  rewritten_title: string;
}

interface FeedCheckInsightResponse {
  insights: FeedCheckInsight[];
  cached: boolean;
}

interface RealDiffRow {
  product_id: string;
  new_title: string | null;
  /**
   * Vorher aus products.original_title, NICHT aus old_title des Ereignisses:
   * old_title war in 39.006 Ereignissen unser eigener frueherer Text
   * (Spec Datenintegritaet Original-Titel 2.1). NULL = kein Original bekannt,
   * dann nur das Nachher. Bereits normalisiert (siehe `originalTitleOf`),
   * die Komponente liest hier nie das rohe Embed.
   */
  original_title: string | null;
}

/**
 * Das PostgREST-Embed `products(original_title)` kommt bei einer Zu-eins-
 * Relation normalerweise als Objekt, kann aber als Array zurueckkommen (z.B.
 * bei einer mehrdeutigen Relation, wenn ein zweiter FK auf `products` dazu-
 * kommt) oder als `null`, wenn das Produkt inzwischen geloescht wurde. Ohne
 * diese Normalisierung waere `original_title` in der Array-Form `undefined`
 * und JEDE Zeile fiele still in den Nachher-only-Zweig -- kein Fehler, keine
 * Warnung, nur ein verlorenes Vorher.
 */
function originalTitleOf(products: unknown): string | null {
  const row = Array.isArray(products) ? products[0] : products;
  const value = (row as { original_title?: string | null } | null | undefined)?.original_title;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * `examples` = Gemini schreibt die drei schwaechsten Titel beispielhaft neu
 * (Erstkontakt, der Feed hat noch keine Optimierung gesehen).
 * `real` = der Feed IST optimiert, also zeigen wir die echten Aenderungen aus
 * `optimization_events`. Kein LLM-Call, keine Kosten. Ein Vorschlag fuer
 * Produkte, die bereits neu geschrieben wurden, waere schlicht falsch.
 */
export type FeedCheckInsightsMode = 'examples' | 'real';

export interface FeedCheckInsightsProps {
  feedId: string | null;
  /** critical_count + warning_count from the snapshot — basis for the
   * information-gap line (research Q10). Optional: without it the line is
   * simply omitted. Nur im Modus `examples` von Belang. */
  weakCount?: number;
  /** Deckel des Optimierungs-Angebots auf dieser Seite (worstTargetCount =
   * Minimum aus verbleibendem Kontingent und dem Bestand, den der Server nach
   * seinem failed-Reset waehlt; seit Task 10 keine feste 200 mehr). Liegt
   * `weakCount` darueber, darf die Luecken-Zeile nicht "ihre optimierten
   * Versionen" fuer ALLE versprechen — der naechste Schritt optimiert nur
   * `optimizeCap` Produkte. Welche das sind, ist bewusst offen: der Server
   * waehlt per Cursor ueber den PK-Index, nicht nach Score (siehe
   * `useFeedCheckOptimize`). Eine "die schwaechsten"-Zusage waere hier falsch. */
  optimizeCap?: number;
  /** Siehe `FeedCheckInsightsMode`. Default `examples`. */
  mode?: FeedCheckInsightsMode;
}

/** The endpoint doesn't cap or dedupe — render at most this many rows. */
const MAX_INSIGHTS = 3;

/**
 * Vorher/Nachher-Block des Reports.
 *
 * Fully silent-degrade in beiden Modi: loading shows skeleton rows, any error
 * (403/404 NO_SCORES/502 INSIGHT_GENERATION_FAILED/network) or an empty
 * result collapses the block to nothing so the rest of the report stays
 * usable. Rendered as a child of `FeedCheckHeader`.
 */
export function FeedCheckInsights({ feedId, weakCount, optimizeCap, mode = 'examples' }: FeedCheckInsightsProps) {
  const { t, i18n } = useTranslation('optimization');
  const viewTracked = useRef(false);
  const isReal = mode === 'real';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['feed-check-insights', feedId],
    queryFn: async (): Promise<FeedCheckInsightResponse> => {
      const { data, error } = await supabase.functions.invoke('feed-check-insight', {
        body: { feed_id: feedId },
      });
      if (error) throw error;
      return data as FeedCheckInsightResponse;
    },
    enabled: !isReal && !!feedId,
    staleTime: Infinity,
    retry: 1,
  });

  // Zustand C: die tatsaechlich geschriebenen Titel. Bewusst nach Zeitpunkt
  // sortiert (die juengsten Aenderungen), nicht nach Score-Zuwachs — der
  // laege nur ueber einen zusaetzlichen Join auf product_scores vor, und die
  // Aussage "das haben wir zuletzt an deinem Feed geaendert" ist die, die der
  // Nutzer an dieser Stelle nachpruefen kann.
  // Vorher kommt aus `products.original_title`; `SuccessScoreboard` und
  // `Phase2Texte` bleiben bei `old_title`/`new_title`, weil sie DIESEN Lauf
  // beschreiben (Spec 5.4).
  const {
    data: realRows,
    isLoading: realLoading,
    isError: realError,
    error: realErrorObject,
  } = useQuery({
    queryKey: ['feed-check-real-diffs', feedId],
    queryFn: async (): Promise<RealDiffRow[]> => {
      const { data, error } = await supabase
        .from('optimization_events')
        .select('product_id, new_title, products(original_title)')
        .eq('feed_id', feedId as string)
        .order('optimized_at', { ascending: false })
        .limit(MAX_INSIGHTS);
      if (error) throw error;
      // Mapping direkt hier, nicht als roher Cast: `originalTitleOf`
      // normalisiert das Embed, bevor die Komponente es je zu Gesicht
      // bekommt.
      return (data ?? []).map((row: { product_id: string; new_title: string | null; products: unknown }) => ({
        product_id: row.product_id,
        new_title: row.new_title,
        original_title: originalTitleOf(row.products),
      }));
    },
    enabled: isReal && !!feedId,
    staleTime: 60 * 1000,
  });

  // WP5 A2O-04: vorher schluckte die Komponente den Fehler und renderte
  // nichts. Ohne Meldung faellt ein Timeout (Audit: 500 auf golfimport)
  // niemandem auf. Das PostgREST-Fehlerobjekt geht als code/message mit, sonst
  // ist ein Timeout (57014) in Sentry nicht von einem RLS-Fehler zu trennen.
  useEffect(() => {
    if (!realError) return;
    const pgError = (realErrorObject ?? {}) as { code?: unknown; message?: unknown };
    Sentry.captureException(new Error('feed-check real diffs failed'), {
      tags: { feature: 'feed-check', action: 'insights-real' },
      extra: {
        feedId,
        code: typeof pgError.code === 'string' ? pgError.code : null,
        message: typeof pgError.message === 'string' ? pgError.message : null,
      },
    });
  }, [realError, realErrorObject, feedId]);

  const insights = (data?.insights ?? []).slice(0, MAX_INSIGHTS);
  // Ohne neuen Titel gibt es nichts zu zeigen; eine leere Nachher-Seite liest
  // sich wie ein Fehler der Optimierung.
  const realDiffs = (realRows ?? []).filter((row) => !!row.new_title);

  useEffect(() => {
    // Das Ereignis misst den Erstkontakt-Ausblick. Im echten Zustand waere es
    // dieselbe Zahl fuer eine andere Sache.
    if (!isReal && !viewTracked.current && insights.length > 0) {
      viewTracked.current = true;
      trackConversion('feed_check_insight_view');
    }
  }, [insights.length, isReal]);

  if (isReal ? realLoading : isLoading) {
    return (
      <div className="space-y-3 print:hidden">
        <span className="text-[11px] font-bold tracking-[0.08em] text-primary mb-2 block">
          {t(isReal ? 'feedCheck.insights.realTitle' : 'feedCheck.insights.title')}
        </span>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (isReal) {
    if (realError || realDiffs.length === 0) return null;

    return (
      <div className="space-y-3">
        <span className="text-[11px] font-bold tracking-[0.08em] text-primary mb-2 block">
          {t('feedCheck.insights.realTitle')}
        </span>
        <ul className="space-y-3">
          {realDiffs.map((row, index) => (
            <li
              // Ein Produkt kann in den juengsten drei Ereignissen mehrfach
              // vorkommen (Re-Optimierung), die Produkt-ID ist hier also kein
              // eindeutiger Schluessel.
              key={`${row.product_id}-${index}`}
              className="bg-card rounded-lg overflow-hidden border border-border"
            >
              <div className="p-3">
                {row.original_title ? (
                  <BeforeAfterDiff before={row.original_title} after={row.new_title} variant="stacked" />
                ) : (
                  <p data-testid="insight-after-only" className="whitespace-pre-wrap text-sm leading-relaxed">
                    {row.new_title}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isError || insights.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <span className="text-[11px] font-bold tracking-[0.08em] text-primary mb-2 block">
        {t('feedCheck.insights.title')}
      </span>
      <ul className="space-y-3">
        {insights.map((insight) => (
          <li
            key={insight.product_id}
            className="bg-card rounded-lg overflow-hidden border border-border"
          >
            <div className="p-3">
              <BeforeAfterDiff before={insight.original_title} after={insight.rewritten_title} variant="stacked" />
            </div>
            {insight.issue && (
              <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                {insight.issue}
              </div>
            )}
          </li>
        ))}
      </ul>
      {typeof weakCount === 'number' && Math.max(0, weakCount - insights.length) > 0 && (
        <p className="text-xs text-muted-foreground">
          {/* `count` waehlt die Pluralform, `formatted`/`capFormatted` tragen
              die Anzeige. Beide Zahlen sind seit dem Wegfall des 200er-Deckels
              fuenfstellig, roh interpoliert stand hier "15074". */}
          {typeof optimizeCap === 'number' && weakCount > optimizeCap
            ? // Mehr Baustellen als das Angebot abdeckt: die Zusage nennt den
              // Deckel, statt optimierte Versionen fuer alle zu versprechen.
              t('feedCheck.insights.gapLineCapped', {
                count: weakCount - insights.length,
                formatted: formatInteger(weakCount - insights.length, i18n?.language),
                cap: optimizeCap,
                capFormatted: formatInteger(optimizeCap, i18n?.language),
              })
            : t('feedCheck.insights.gapLine', {
                count: weakCount - insights.length,
                formatted: formatInteger(weakCount - insights.length, i18n?.language),
              })}
        </p>
      )}
    </div>
  );
}
